// Estratégia de SAÍDA de encalhe — sugere a ação para girar/liberar itens
// parados (com saldo e sem venda), usando dados que já existem no produto
// (margem, curva ABC, dias sem venda, consignação). Função pura e testável.

export type EncalheAction = 'Promoção' | 'Liquidação' | 'Devolução' | 'Consignação'

export interface EncalheSuggestion {
  action: EncalheAction
  reason: string
  /** Capital imobilizado que a ação libera (saldo × custo). */
  capital: number
}

export interface EncalheInput {
  currentStock: number
  cost: number | null
  marginPct: number | null
  abcCurve: 'A' | 'B' | 'C' | null
  /** Dias desde a última venda; null = nunca vendeu. */
  daysWithoutSale: number | null
  isConsigned?: boolean
}

/** Dias desde `iso`, usando um `nowMs` estável (capturado 1x no mount). null se sem data. */
export function daysSince(iso: string | null, nowMs: number): number | null {
  if (!iso) return null
  return Math.floor((nowMs - new Date(iso).getTime()) / 86_400_000)
}

/**
 * Sugere a ação de saída para um item encalhado. Regra determinística:
 * - Consignado parado → Devolução (sem custo de devolver).
 * - Nunca vendeu → Devolução (sem demanda comprovada).
 * - Margem ≥ 30% → Promoção (desconto leve gira sem zerar o lucro).
 * - Margem < 10% → Devolução (desconto não compensa; negociar com fornecedor).
 * - Curva C ou > 180 dias parado → Liquidação (liberar caixa rápido).
 * - Caso geral → Promoção (estimular giro).
 */
export function suggestEncalheExit(i: EncalheInput): EncalheSuggestion {
  const capital = Math.max(0, i.currentStock) * (i.cost ?? 0)
  const m = i.marginPct ?? 0
  const d = i.daysWithoutSale

  let action: EncalheAction
  let reason: string
  if (i.isConsigned) {
    action = 'Devolução'; reason = 'Consignado parado — devolver ao fornecedor sem custo.'
  } else if (d == null) {
    action = 'Devolução'; reason = 'Nunca vendeu — sem demanda; negociar troca/devolução.'
  } else if (m >= 30) {
    action = 'Promoção'; reason = 'Margem folgada — um desconto leve gira sem perder lucro.'
  } else if (m < 10) {
    action = 'Devolução'; reason = 'Margem baixa — desconto não compensa; negociar com o fornecedor.'
  } else if (i.abcCurve === 'C' || d > 180) {
    action = 'Liquidação'; reason = 'Baixo giro — liquidar para liberar caixa rápido.'
  } else {
    action = 'Promoção'; reason = 'Estimular o giro com um desconto pontual.'
  }
  return { action, reason, capital }
}

/** Cores dos badges de ação (dual-theme). */
export const ENCALHE_ACTION_BADGE: Record<EncalheAction, string> = {
  'Promoção':    'bg-brand-500/15 text-brand-700 dark:text-brand-300',
  'Liquidação':  'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300',
  'Devolução':   'bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300',
  'Consignação': 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
}
